import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MdAdd, MdDelete, MdEdit } from "react-icons/md";
import Breadcrumbs from "../../../components/BreadCrumbs";
import Button from "../../../components/buttons/Button";
import CustomLoading from "../../../components/customLoading/CustomLoading";
import Table from "../../../components/tables/Table";
import ShowModal from "../../../utils/ShowModal";
import DateRangePicker from "../../../components/inputFields/DateRangePicker";
import { searchStaffUsers } from "../../../services/search";
import {
    createB2cSalespersonDailyCallStats,
    deleteB2cSalespersonDailyCallStats,
    getB2cSalespersonDailyCallStatsList,
    patchB2cSalespersonDailyCallStats,
} from "../../../services/b2c_salesperson_daily_call_stats";

const emptyForm = () => ({
    sales_person: "",
    date: "",
    total_calls: 0,
    total_talk_time: 0,
    tracked_calls: 0,
    tracked_talk_time: 0,
    untracked_calls: 0,
    untracked_talk_time: 0,
    incoming_calls: 0,
    outgoing_calls: 0,
    missed_calls: 0,
    clock_in_time: "",
    clock_out_time: "",
});

const formatSec = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v) || v <= 0) return "—";
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    if (h) return `${h}h ${m}m`;
    return `${m}m`;
};

const toTimeInput = (v) => {
    if (v == null || v === "") return "";
    const s = String(v);
    if (s.length >= 5 && /^\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
    return s;
};

const displayPerson = (row) => {
    const sp = row.sales_person;
    if (sp && typeof sp === "object") {
        return sp.full_name || sp.name || sp.email || sp.phone || sp.id || "—";
    }
    return row.sales_person_name || row.salesperson_name || sp || "—";
};

const normalizeRows = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.results)) return data.results;
    return [];
};

function B2CSalespersonDailyCallStats() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const emptyFilters = { sales_person: "", date: "", date__gte: "", date__lte: "" };
    const [filterDraft, setFilterDraft] = useState(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const [staffSearch, setStaffSearch] = useState("");
    const [staffHits, setStaffHits] = useState([]);
    const [staffPickOpen, setStaffPickOpen] = useState(false);

    const pageSize = 25;

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                page_size: pageSize,
                ...appliedFilters,
            };
            const res = await getB2cSalespersonDailyCallStatsList(params);
            if (res.status !== 200) {
                toast.error("Failed to load daily call stats");
                return;
            }
            const payload = res.data;
            setRows(normalizeRows(payload));
            setCount(typeof payload.count === "number" ? payload.count : normalizeRows(payload).length);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load daily call stats");
        } finally {
            setLoading(false);
        }
    }, [page, appliedFilters]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    useEffect(() => {
        if (!staffSearch.trim() || staffSearch.length < 2) {
            setStaffHits([]);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const res = await searchStaffUsers({ query: `?search=${encodeURIComponent(staffSearch.trim())}` });
                if (res.status === 200) {
                    const list = res.data?.results || res.data || [];
                    setStaffHits(Array.isArray(list) ? list : []);
                }
            } catch {
                setStaffHits([]);
            }
        }, 350);
        return () => clearTimeout(t);
    }, [staffSearch]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setStaffSearch("");
        setStaffHits([]);
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setEditingId(row.id);
        setForm({
            sales_person: typeof row.sales_person === "object" ? row.sales_person.id : row.sales_person || "",
            date: row.date || "",
            total_calls: row.total_calls ?? 0,
            total_talk_time: row.total_talk_time ?? 0,
            tracked_calls: row.tracked_calls ?? 0,
            tracked_talk_time: row.tracked_talk_time ?? 0,
            untracked_calls: row.untracked_calls ?? 0,
            untracked_talk_time: row.untracked_talk_time ?? 0,
            incoming_calls: row.incoming_calls ?? 0,
            outgoing_calls: row.outgoing_calls ?? 0,
            missed_calls: row.missed_calls ?? 0,
            clock_in_time: toTimeInput(row.clock_in_time),
            clock_out_time: toTimeInput(row.clock_out_time),
        });
        setStaffSearch(displayPerson(row));
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const payloadFromForm = () => {
        const body = {
            total_calls: Number(form.total_calls) || 0,
            total_talk_time: Number(form.total_talk_time) || 0,
            tracked_calls: Number(form.tracked_calls) || 0,
            tracked_talk_time: Number(form.tracked_talk_time) || 0,
            untracked_calls: Number(form.untracked_calls) || 0,
            untracked_talk_time: Number(form.untracked_talk_time) || 0,
            incoming_calls: Number(form.incoming_calls) || 0,
            outgoing_calls: Number(form.outgoing_calls) || 0,
            missed_calls: Number(form.missed_calls) || 0,
        };
        if (form.clock_in_time) body.clock_in_time = `${form.clock_in_time}:00`;
        else body.clock_in_time = null;
        if (form.clock_out_time) body.clock_out_time = `${form.clock_out_time}:00`;
        else body.clock_out_time = null;

        if (!editingId) {
            body.sales_person = form.sales_person;
            body.date = form.date;
        }
        return body;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!editingId && (!form.sales_person || !form.date)) {
            toast.error("Salesperson and date are required");
            return;
        }
        setSubmitting(true);
        try {
            const body = payloadFromForm();
            const res = editingId
                ? await patchB2cSalespersonDailyCallStats(editingId, body)
                : await createB2cSalespersonDailyCallStats(body);
            if (res.status === 200 || res.status === 201) {
                toast.success(editingId ? "Updated" : "Created");
                closeModal();
                loadList();
            } else {
                toast.error(res.data?.detail || "Request failed");
            }
        } catch (err) {
            const d = err.response?.data;
            toast.error(typeof d === "string" ? d : d?.detail || "Save failed");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this daily stats row?")) return;
        try {
            const res = await deleteB2cSalespersonDailyCallStats(id);
            if (res.status === 204 || res.status === 200) {
                toast.success("Deleted");
                loadList();
            } else toast.error("Delete failed");
        } catch {
            toast.error("Delete failed");
        }
    };

    const totalPages = Math.max(1, Math.ceil(count / pageSize));

    const tableHeaders = useMemo(
        () => [
            { label: "Date", key: "date" },
            { label: "Salesperson", key: "sp" },
            { label: "Calls", key: "calls" },
            { label: "Talk (s)", key: "talk" },
            { label: "Tracked", key: "tr" },
            { label: "Untracked", key: "utr" },
            { label: "Clock", key: "clk" },
            { label: "", key: "actions" },
        ],
        [],
    );

    const tableRows = rows.map((row) => ({
        date: <span className="text-sm font-medium text-gray-900">{row.date}</span>,
        sp: <span className="text-sm text-gray-700">{displayPerson(row)}</span>,
        calls: (
            <span className="text-sm">
                {row.total_calls ?? 0}
                <span className="text-gray-400 text-xs ml-1">/ tr {row.tracked_calls ?? 0}</span>
            </span>
        ),
        talk: <span className="text-sm">{formatSec(row.total_talk_time)}</span>,
        tr: <span className="text-xs text-emerald-700">{formatSec(row.tracked_talk_time)}</span>,
        utr: <span className="text-xs text-amber-800">{formatSec(row.untracked_talk_time)}</span>,
        clk: (
            <span className="text-xs text-gray-600">
                {toTimeInput(row.clock_in_time) || "—"} → {toTimeInput(row.clock_out_time) || "—"}
            </span>
        ),
        actions: (
            <div className="flex gap-1">
                <button type="button" onClick={() => openEdit(row)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit">
                    <MdEdit size={18} />
                </button>
                <button type="button" onClick={() => handleDelete(row.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md" aria-label="Delete">
                    <MdDelete size={18} />
                </button>
            </div>
        ),
    }));

    const numField = (key, label) => (
        <div>
            <label className="block text-xs font-medium text-gray-600 mb-0.5">{label}</label>
            <input
                type="number"
                min={0}
                className="w-full px-2 py-1.5 text-sm rounded border border-gray-200"
                value={form[key]}
                onChange={(e) => setForm({ ...form, [key]: e.target.value })}
            />
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
                <Breadcrumbs
                    items={[
                        { name: "Home", link: "/dashboard" },
                        { name: "Leads", link: "/leads-management" },
                        { name: "B2C" },
                        { name: "Daily call stats" },
                    ]}
                />
                <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">B2C salesperson daily call stats</h1>
                        <p className="text-xs text-gray-500 mt-0.5">List, create, update, and delete aggregated daily call rows.</p>
                    </div>
                    <Button type="button" onClick={openCreate} className="bg-emerald-600 text-white px-3 py-2 text-sm rounded-md hover:bg-emerald-700 inline-flex items-center gap-1">
                        <MdAdd size={18} /> Add row
                    </Button>
                </div>

                <div className="mt-3 flex flex-wrap gap-2 items-end">
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-500 uppercase">Sales person ID</label>
                        <input
                            className="px-2 py-1.5 text-sm border rounded w-44"
                            placeholder="UUID filter"
                            value={filterDraft.sales_person}
                            onChange={(e) => setFilterDraft({ ...filterDraft, sales_person: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-500 uppercase">Date</label>
                        <input
                            type="date"
                            className="px-2 py-1.5 text-sm border rounded"
                            value={filterDraft.date}
                            onChange={(e) => setFilterDraft({ ...filterDraft, date: e.target.value })}
                        />
                    </div>
                    <div className="flex flex-col gap-0.5 min-w-[200px]">
                        <label className="block text-[10px] font-semibold text-gray-500 uppercase">Date Range</label>
                        <DateRangePicker
                            compact
                            value={{ start: filterDraft.date__gte, end: filterDraft.date__lte }}
                            onChange={({ start, end }) => {
                                setFilterDraft(prev => ({
                                    ...prev,
                                    date__gte: start || '',
                                    date__lte: end || ''
                                }));
                            }}
                            placeholder="Choose start & end date"
                        />
                    </div>
                    <Button
                        type="button"
                        className="bg-slate-700 text-white px-3 py-1.5 text-sm rounded-md"
                        onClick={() => {
                            setAppliedFilters({ ...filterDraft });
                            setPage(1);
                        }}
                    >
                        Apply
                    </Button>
                    <Button
                        type="button"
                        className="border border-gray-300 px-3 py-1.5 text-sm rounded-md"
                        onClick={() => {
                            setFilterDraft(emptyFilters);
                            setAppliedFilters(emptyFilters);
                            setPage(1);
                        }}
                    >
                        Clear
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-3">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <CustomLoading size={64} />
                    </div>
                ) : (
                    <Table headers={tableHeaders} rows={tableRows} />
                )}
                {count > pageSize && (
                    <div className="flex items-center justify-between mt-2 text-xs text-gray-600">
                        <span>
                            Page {page} / {totalPages} ({count} rows)
                        </span>
                        <div className="flex gap-2">
                            <Button type="button" disabled={page <= 1} className="px-2 py-1 border rounded text-xs" onClick={() => setPage((p) => Math.max(1, p - 1))}>
                                Prev
                            </Button>
                            <Button
                                type="button"
                                disabled={page >= totalPages}
                                className="px-2 py-1 border rounded text-xs"
                                onClick={() => setPage((p) => p + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            <ShowModal
                isOpen={modalOpen}
                onRequestClose={closeModal}
                contentLabel={editingId ? "Edit daily call stats" : "Create daily call stats"}
                modalWidth="min(720px, 96vw)"
                componentToPass={
                    <form onSubmit={handleSubmit} className="p-4 max-h-[85vh] overflow-y-auto">
                        <h2 className="text-lg font-bold text-gray-900 mb-3">{editingId ? "Edit row" : "New row"}</h2>

                        {!editingId ? (
                            <div className="grid gap-3 sm:grid-cols-2 mb-3">
                                <div className="sm:col-span-2 relative">
                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Salesperson (search)</label>
                                    <input
                                        className="w-full px-2 py-1.5 text-sm border rounded"
                                        placeholder="Type name / phone…"
                                        value={staffSearch}
                                        onChange={(e) => {
                                            setStaffSearch(e.target.value);
                                            setStaffPickOpen(true);
                                        }}
                                        onFocus={() => setStaffPickOpen(true)}
                                    />
                                    {staffPickOpen && staffHits.length > 0 && (
                                        <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded border border-gray-200 bg-white shadow text-sm">
                                            {staffHits.map((u) => (
                                                <li key={u.id}>
                                                    <button
                                                        type="button"
                                                        className="w-full text-left px-2 py-1.5 hover:bg-gray-50"
                                                        onClick={() => {
                                                            setForm({ ...form, sales_person: u.id });
                                                            setStaffSearch(u.full_name || u.name || u.email || u.phone || u.id);
                                                            setStaffPickOpen(false);
                                                        }}
                                                    >
                                                        {u.full_name || u.name || u.email || u.id}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    <p className="text-[10px] text-gray-400 mt-0.5">Selected ID: {form.sales_person || "—"}</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Date</label>
                                    <input
                                        type="date"
                                        required
                                        className="w-full px-2 py-1.5 text-sm border rounded"
                                        value={form.date}
                                        onChange={(e) => setForm({ ...form, date: e.target.value })}
                                    />
                                </div>
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 mb-2">Salesperson and date are fixed for this row (unique per day).</p>
                        )}

                        <div className="grid gap-2 sm:grid-cols-2 border-t border-gray-100 pt-3">
                            {numField("total_calls", "Total calls")}
                            {numField("total_talk_time", "Total talk time (seconds)")}
                            {numField("tracked_calls", "Tracked calls")}
                            {numField("tracked_talk_time", "Tracked talk (seconds)")}
                            {numField("untracked_calls", "Untracked calls")}
                            {numField("untracked_talk_time", "Untracked talk (seconds)")}
                            {numField("incoming_calls", "Incoming")}
                            {numField("outgoing_calls", "Outgoing")}
                            {numField("missed_calls", "Missed")}
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Clock in</label>
                                <input
                                    type="time"
                                    className="w-full px-2 py-1.5 text-sm border rounded"
                                    value={form.clock_in_time}
                                    onChange={(e) => setForm({ ...form, clock_in_time: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Clock out</label>
                                <input
                                    type="time"
                                    className="w-full px-2 py-1.5 text-sm border rounded"
                                    value={form.clock_out_time}
                                    onChange={(e) => setForm({ ...form, clock_out_time: e.target.value })}
                                />
                            </div>
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">Working hours are computed on the server when both clock times are set.</p>

                        <div className="flex gap-2 mt-4">
                            <Button type="button" className="flex-1 border border-gray-300 py-2 text-sm rounded-md" onClick={closeModal}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={submitting} className="flex-1 bg-emerald-600 text-white py-2 text-sm rounded-md disabled:opacity-50">
                                {submitting ? "Saving…" : editingId ? "Update" : "Create"}
                            </Button>
                        </div>
                    </form>
                }
            />
        </div>
    );
}

export default B2CSalespersonDailyCallStats;
